import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TPipelineExecutionStatusProps } from '@/console/features/pipelines/components/pipeline-execution-status/pipeline-execution-status.types';
import { CalendarIcon } from '@/shared/assets/icons/calendar-icon';
import { ClockIcon } from '@/shared/assets/icons/clock-icon';
import { Chip } from '@/shared/components/ui/chip/chip';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadPipelineExecutionsForPipelineQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  PipelineExecutionStatusEnum,
  ReadPipelineExecutionsForPipelineQuery,
} from '@/shared/types/types.generated';
import { formatDuration } from '@/shared/utils/format';

const PIPELINE_EXECUTION_STATUS_POLLING_INTERVAL_MS = 30_000;

export const PipelineExecutionStatus: React.FC<
  TPipelineExecutionStatusProps
> = ({
  pipelineConfigurationId,
  execution,
  isLoading: isExecutionLoading = false,
  showDetails = false,
}) => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();
  const { getStatusColor, getStatusLabel, isJobRunning } = useJobStatus();

  const shouldStopPolling = (data: ReadPipelineExecutionsForPipelineQuery) => {
    return data?.readAllPipelineExecutionsForConfiguration.items.every(
      (item) => !isJobRunning(item.status),
    );
  };

  const { data, isLoading } = usePollingQuery({
    useQueryHook: useReadPipelineExecutionsForPipelineQuery,
    queryArg: {
      pipelineConfigurationId: pipelineConfigurationId!,
      pagination: { pageSize: 1, page: 1 },
    },
    // A caller-supplied execution (e.g. from a batched list fetch) short-circuits this
    // component's own per-pipeline fetch/poll.
    queryOptions: { skip: !pipelineConfigurationId || !!execution },
    shouldStopPolling,
    pollingInterval: PIPELINE_EXECUTION_STATUS_POLLING_INTERVAL_MS,
  });

  const pipelineExecution =
    execution ?? data?.readAllPipelineExecutionsForConfiguration.items[0];

  const hasTimeInfo =
    pipelineExecution?.duration && pipelineExecution?.updateTimestamp;

  const getChipText = (status?: PipelineExecutionStatusEnum) => {
    if (isLoading || isExecutionLoading) {
      return t('common:loading');
    }

    if (status) {
      return getStatusLabel(status);
    }

    return t('pipelines:no_executions');
  };

  const renderChip = () => {
    return (
      <Chip
        status={getStatusColor(pipelineExecution?.status)}
        label={getChipText(pipelineExecution?.status)}
        withDot
      />
    );
  };

  if (!showDetails || !hasTimeInfo) {
    return renderChip();
  }

  return (
    <Stack gap="0.375rem" paddingY="0.5rem" direction="row">
      {renderChip()}
      <Stack gap="0.375rem" direction="row" alignItems="center">
        <ClockIcon variant="primary" />
        <Typography variant="p10" color="textSecondary">
          {formatDuration(t, pipelineExecution?.duration)}
        </Typography>
      </Stack>
      <Stack gap="0.375rem" direction="row" alignItems="center">
        <CalendarIcon variant="primary" />
        <Typography variant="p10" color="textSecondary">
          {formatLocalTime(pipelineExecution?.updateTimestamp)}
        </Typography>
      </Stack>
    </Stack>
  );
};
